import type { AxisSelection } from '@atlas/types'
import { AxisCode, ClassifiedObjectType } from '@atlas/types'
import { useQuery } from '@tanstack/react-query'
import type { Message } from '@/domain/message.types'
import { classificationApi } from '@/services/classification.service'
import { messageApi } from '@/services/message.service'

/** Root of the campaign-messages key family. Exported so mutations invalidate the same key. */
export const CAMPAIGN_MESSAGES_KEY = 'campaign-messages'

/** Query key for a Campaign's associated messages (discovered by campaignCode). */
const campaignMessagesKey = (campaignCode: string) => [CAMPAIGN_MESSAGES_KEY, campaignCode]

/**
 * The messages associated with a Campaign — discovered by the `campaign_code`
 * attribute the association picker stamps on each message (the same
 * mechanism the Files section uses for its own association), mirroring
 * {@link useWorkOrderMessages}'s `order_code` read exactly. `campaign_code` is
 * an `object_attribute` (free-text), NOT a dimension, so it is passed as an
 * ATTRIBUTE filter. The classification search returns bare message refs; we
 * hydrate each to its full record for display. Empty until at least one
 * message has been associated with this Campaign.
 *
 * Hydration does NOT swallow per-message failures: a failed `get` rejects the
 * whole query so react-query reports `isError` — this is an accepted,
 * inherited limitation (design.md D3 of campaign-message-translations): a
 * single soft-deleted-but-still-tagged message breaks the whole section into
 * an error state, matching the same pre-existing behavior `useWorkOrderMessages`
 * already has for Work Orders. Kept out of the generic `['messages']` cache so
 * the global list and this campaign-scoped view invalidate independently.
 */
export const useCampaignMessages = (campaignCode: string | null) =>
	useQuery<Message[]>({
		queryKey: campaignMessagesKey(campaignCode ?? ''),
		queryFn: async () => {
			const refs = await classificationApi.search(ClassifiedObjectType.MESSAGE, {}, {
				[AxisCode.CAMPAIGN_CODE]: [campaignCode as string],
			} as AxisSelection)
			return Promise.all(refs.map((r) => messageApi.get(r.objectId)))
		},
		enabled: !!campaignCode,
	})
